import React, { useContext, useState } from 'react';
import { GameModel } from '../types/GamesModel';
import { Platform } from '../types/Platform';
import { Alert, Button } from 'react-bootstrap';
import { SyntheticEvent } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar, faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';

import { PulseLoader } from 'react-spinners';
import { useAppDispatch, useAppSelector } from '../store';
import { getGames } from '../reducers/gamesReducer';
import GamesAPI from '../api/GamesAPI';
import { setGameIdForModal } from '../reducers/uiReducer';
import { describeEvaluationError } from './evaluationError';
import AppContext from '../context/AppContext';

// The overlay's action row: Star the game · Delete (manually added games only) · platform Analyse link.
// (Re-)evaluate is GamesOverlayEvaluate, at the top of the moves panel.
export default function GamesListItemEditorOptions(props: { game: GameModel }): React.ReactNode {
  const { game } = props;
  const dispatch = useAppDispatch();
  const { isMobile } = useContext(AppContext);
  const params = useAppSelector((state) => state.paramsReducer.params);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [starred, setStarred] = useState(game.Starred);
  const [starToast, setStarToast] = useState('');

  const onStar = () => {
    const next = !starred;
    // Flip instantly; the server call happens in the background.
    setStarred(next);
    setStarToast(next ? 'Game marked as starred' : 'Game unstarred');
    window.setTimeout(() => setStarToast(''), 1600);
    GamesAPI.updateUserGame(game.Id, next);
  };

  const onDeleteClick = async (e: SyntheticEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await GamesAPI.deleteGame(game.Id);
      dispatch(setGameIdForModal(null));
      dispatch(getGames(params));
      setLoading(false);
    } catch (error) {
      setError(error.message);
      setLoading(false);
    }
  };

  return (
    <>
      {loading || error ? (
        <div className="d-flex flex-row align-items-center">
          {error ? (
            <Alert variant="danger">
              <div>{describeEvaluationError(error).message}</div>
              <div className="small text-muted">{describeEvaluationError(error).hint}</div>
            </Alert>
          ) : null}
          <PulseLoader loading={loading} color="#ffffff"></PulseLoader>
        </div>
      ) : null}
      <div className="cm-game-actions-row d-flex flex-row align-items-center gap-2">
        <span className="cm-game-star-wrap">
          <button
            type="button"
            className={`cm-game-star-btn${starred ? ' active' : ''}`}
            onClick={onStar}
            aria-pressed={starred}
            title={starred ? 'Unstar this game' : 'Star (favorite this game)'}
          >
            <FontAwesomeIcon icon={faStar} />
          </button>
          {starToast ? <span className="cm-cp-toast">{starToast}</span> : null}
        </span>
        {game.Platform == Platform.None ? (
          <Button variant="outline-danger" size="sm" disabled={loading} onClick={onDeleteClick}>
            Delete
          </Button>
        ) : null}
        {game.Platform == Platform.ChessCom || game.Platform == Platform.Lichess ? (
          <span className="cm-analyse-link ms-auto">
            {game.Platform == Platform.ChessCom ? (
              <a
                target="blank"
                href={game.Link?.replace('game', 'analysis/game')}
                className="cm-analyse-anchor"
              >
                {isMobile ? null : 'Analyse in ♟️ '}
                Chess.com <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="ms-1" />
              </a>
            ) : game.Platform == Platform.Lichess ? (
              <a target="blank" href={game.Link} className="cm-analyse-anchor">
                {isMobile ? null : 'Analyse in ♟️ '}
                Lichess <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="ms-1" />
              </a>
            ) : null}
          </span>
        ) : null}
      </div>
    </>
  );
}
